<template>
  <template v-if="!isAddStageBtn">
    <div class="StageItemCard flex flex-col md-flex-row">
      <StageItemImage
        :img-url="fullStageLogoURL"
        :img-placeholder="imgPlaceholder"
      />
      <div class="flex flex-col space-between pa-16">
        <div>
          <router-link
            class="StageItemCard__link"
            :to="{
              path: '/sso',
              query: { fqdn: fqdn, path: '/panel/', lang: $i18n.locale }
            }"
            target="_blank"
          >
            {{ fqdn }}
          </router-link>

          <div class="StageItemCard__owner">
            {{ artistFullName }}
          </div>
        </div>
        <div class="StageItemCard__members">
          <ul>
            <li
              v-for="(member, index) in stageMembersListMaxFive"
              :key="`avatar-${index}`"
              :style="{
                left: index > 0 ? `-${index * 6}px` : '0',
                zIndex: stageMembersListMaxFive.length - index
              }"
            >
              <TooltipComponent
                anchor="top middle"
                self="bottom middle"
                teleport-target="#teleports"
              >
                {{
                  employeeFullname(
                    member.employee.firstName,
                    member.employee.lastName
                  )
                }}
              </TooltipComponent>
              <template v-if="member.employee.avatar">
                <img
                  :ref="`member-${index}`"
                  alt="Stage member profile picture"
                  :src="fullProfileURL(member.employee.avatar)"
                />
              </template>
              <div v-else class="StageItemCard__members__initials">
                <span>
                  {{
                    employeeInitials(
                      member.employee.firstName,
                      member.employee.lastName
                    )
                  }}</span
                >
              </div>
            </li>
            <li
              v-if="hasMoreThanFiveMembers"
              class="StageItemCard__members__extra-members-number"
              @click="openMembersModal"
            >
              {{ membersCount }}+
            </li>
          </ul>
        </div>
      </div>
    </div>
  </template>
  <template v-else>
    <router-link :to="{ name: 'CreateStageView' }">
      <div class="StageItemCard StageItemCard__add flex flex-col md-flex-row">
        <div class="StageItemCard__add__btn flex flex-col items-center">
          <div class="flex justify-center items-center">
            <span class="icon-plus"></span>
          </div>
          <span class="mt-8">
            {{ $t('add_stage') }}
          </span>
        </div>
      </div>
    </router-link>
  </template>
</template>

<script setup>
import { computed } from 'vue'
import StageItemImage from '@/components/stage-item/StageItemCardImage.vue'
import { TooltipComponent } from '@snowshoe-frontend/tooltip-component'

const props = defineProps({
  isAddStageBtn: {
    type: Boolean,
    default: false
  },
  fqdn: {
    type: String,
    default: ''
  },
  logoUrl: {
    type: String,
    default: ''
  },
  artistFullName: {
    type: String,
    default: ''
  },
  stageMembersList: {
    type: Array,
    default: () => []
  }
})

const imageProxyURL = import.meta.env.VITE_IMAGEPROXY_URL

const stageMembersListMaxFive = computed(() =>
  props.stageMembersList.slice(0, 5)
)

const hasMoreThanFiveMembers = computed(() => props.stageMembersList.length > 5)

const membersCount = computed(() => props.stageMembersList.length - 5)

const fullStageLogoURL = computed(() =>
  props.logoUrl
    ? `${imageProxyURL}/insecure/rs:fit:650:650/sm:0/plain/${props.logoUrl}@png`
    : ''
)

const imgPlaceholder = computed(() => {
  const parts = props.fqdn.split('-')
  return parts.length > 1 ? `${parts[0][0]} ${parts[1][0]}` : props.fqdn[0]
})

const employeeInitials = computed(() => (firstName = '', lastName = '') => {
  return firstName[0] + lastName[0]
})

const employeeFullname = computed(() => (firstName = '', lastName = '') => {
  return `${firstName} ${lastName}`
})

async function openMembersModal() {
  // ToDo: create new dialog with members
}

function fullProfileURL(profile_picture_url) {
  return `${imageProxyURL}/insecure/rs:fill:150:150/sm:0/plain/${profile_picture_url}@jpg`
}
</script>

<style lang="scss" scoped>
.StageItemCard {
  background-color: var(--color-secondary);
  border-radius: rem(16);
  box-shadow: 0 var(--grid-4) 6px rgba(0, 0, 0, 0.04);
  overflow: hidden;

  @include tablet {
    height: rem(152);
  }

  &__add {
    &__btn {
      div {
        width: var(--grid-40);
        height: var(--grid-40);
        border-radius: 50%;
        border: 2px solid var(--color-gray-3);
      }
    }
  }

  &__link {
    color: var(--color-primary);
    font-weight: var(--font-weight-semibold);
    font-size: rem(16);
    margin-bottom: var(--grid-8);
    display: block;

    @include desktop {
      font-size: rem(20);
    }
  }

  &__owner {
    color: var(--color-gray-3);
    font-weight: var(--font-weight-semibold);
    font-size: rem(12);

    @include desktop {
      font-size: rem(16);
    }
  }

  &__members {
    ul {
      display: flex;
      position: relative;

      li {
        width: rem(32);
        height: rem(32);
        position: relative;
        border-radius: 50%;
        color: var(--color-secondary);

        &:first-child {
          background-color: var(--color-brand-purple);
        }
        &:nth-child(2) {
          background-color: var(--color-brand-purple-2);
        }
        &:nth-child(3) {
          background-color: var(--color-brand-red);
        }
        &:nth-child(4) {
          background-color: var(--color-brand-green);
        }
        &:nth-child(5) {
          background-color: var(--color-brand-pink);
        }

        img {
          width: 100%;
          height: 100%;
          object-fit: cover;
          border-radius: 50%;
          border: 1px solid var(--color-brand-red);
        }
      }
    }

    &__extra-members-number {
      background-color: var(--color-gray-5);
      color: var(--color-secondary);
      border-radius: 100%;
      font-size: 11px;
      display: flex;
      justify-content: center;
      align-items: center;
      cursor: pointer;
    }

    &__initials {
      display: grid;
      place-items: center;
      height: 100%;
      width: 100%;
      color: var(--color-secondary);
      font-size: var(--font-size-s);
    }
  }

  &__add {
    padding: rem(16);
    background-color: transparent;
    border: 2px dashed var(--color-gray-3);
    align-items: center;
    justify-content: center;
    cursor: pointer;
    transition: all 0.2s;
    color: var(--color-gray-3);

    &:hover {
      opacity: 0.6;
    }
  }
}
</style>
